import { HttpErrorResponse, HttpInterceptorFn, HttpRequest } from '@angular/common/http';
import { inject } from '@angular/core';
import { Router } from '@angular/router';
import { catchError, from, switchMap, throwError } from 'rxjs';
import { RiderAuthService } from './rider-auth.service';

/** Auth calls that must never trigger the refresh/replay loop — they 401 on bad credentials
 *  (login) or would recurse (refresh/logout), and OTP/reset flows surface their own errors. */
const NO_RETRY = /\/api\/rider-auth\/(login|register|refresh|logout|otp\/|forgot-password\/)/i;

/**
 * Attaches the rider token to rider API calls (/api/rider…) when signed in. Mirrors the
 * partner/customer interceptors: PROACTIVELY refreshes an already-expired access token before
 * sending, refreshes REACTIVELY on a server 401 (one shared in-flight refresh), and on refresh
 * failure clears the session and redirects to the rider login — so a rider mid-shift never sits
 * on a dead session that "looks" signed in.
 */
export const riderAuthInterceptor: HttpInterceptorFn = (req, next) => {
  const auth = inject(RiderAuthService);
  const router = inject(Router);

  const token = auth.accessToken;
  const isRiderApi = req.url.includes('/api/rider');
  if (!token || !isRiderApi) return next(req);

  const withToken = (r: HttpRequest<unknown> = req) =>
    r.clone({ setHeaders: { Authorization: `Bearer ${auth.accessToken!}` } });
  const noRetry = NO_RETRY.test(req.url);

  const endSession = (err?: HttpErrorResponse) => {
    auth.clearSession();
    router.navigate(['/rider/login'], { queryParams: { reason: 'session-expired' } });
    return throwError(() => err ?? new HttpErrorResponse({ status: 401, statusText: 'Session expired' }));
  };

  const send = () =>
    next(withToken()).pipe(
      catchError((err: HttpErrorResponse) => {
        if (err.status === 401 && !noRetry) {
          return from(auth.refreshAccessToken()).pipe(
            switchMap((ok) => (ok ? next(withToken()) : endSession(err))),
          );
        }
        return throwError(() => err);
      }),
    );

  if (!noRetry && auth.isTokenExpired()) {
    return from(auth.refreshAccessToken()).pipe(
      switchMap((ok) => (ok ? send() : endSession())),
    );
  }

  return send();
};
